// 🚀 Warm-up hook for SRT server
import { useEffect, useRef } from 'react';
import { warmupSrtServer } from '@/utils/api';

export const useWarmup = () => {
  const hasWarmedUp = useRef(false);
  const warmupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only warm-up once per session
    if (hasWarmedUp.current) {
      return;
    }

    // Wait for page to be fully loaded
    const warmupDelay = 2000; // 2 seconds after page load
    
    warmupTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🔥 Page loaded, starting SRT warm-up...');
        const result = await warmupSrtServer();
        
        if (result.success) {
          console.log('✅ SRT server warmed up successfully');
          hasWarmedUp.current = true;
        } else {
          console.warn('⚠️ SRT warm-up failed or skipped:', result.message);
          // Don't mark as warmed up if it failed, so we can retry later
        }
      } catch (error) {
        console.error('🔥 SRT warm-up error:', error);
      }
    }, warmupDelay);

    // Cleanup timeout on unmount
    return () => {
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
      }
    };
  }, []);

  // Manual warm-up function for retry
  const retryWarmup = async () => {
    try {
      console.log('🔥 Manual SRT warm-up retry...');
      const result = await warmupSrtServer();
      
      if (result.success) {
        console.log('✅ SRT server warmed up successfully (retry)');
        hasWarmedUp.current = true;
      } else {
        console.warn('⚠️ SRT warm-up retry failed:', result.message);
      }
      
      return result;
    } catch (error) {
      console.error('🔥 SRT warm-up retry error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return {
    hasWarmedUp: hasWarmedUp.current,
    retryWarmup
  };
};
